/**
 * Detects if the hostname is a local IP.
 *
 * NOTE: `tauri.localhost` is intentionally **excluded**. It's the WebView's
 * own internal origin in Tauri v2 — NOT a local-network host that maps to
 * the backend. Treating it as same-origin caused every API call to be
 * routed to `https://tauri.localhost/api/...`, which returns the SPA's
 * `index.html` (the famous `Unexpected token '<', "<!doctype "...` JSON
 * parse failure). Tauri builds must fall through to the explicit
 * `serverUrl` (`VITE_API_URL`) and PowerSync URL.
 */
export function isLocalIP(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    hostname.startsWith("172.") ||
    hostname.endsWith(".local") ||
    // `<app>.localhost` (local nginx proxy, infra/LOCAL_DEV.md §2b): browsers
    // resolve *.localhost to loopback; it must use same-origin (Vite proxy) so the
    // BFF cookie remains first-party. EXCEPTION: `tauri.localhost` is the
    // Tauri WebView origin (desktop/mobile) and MUST fall through to the explicit
    // serverUrl—if it returned its own origin, calls would 404 in the SPA.
    (hostname.endsWith(".localhost") && hostname !== "tauri.localhost")
  );
}

/**
 * True when `location` is the WebView origin of a PACKAGED native build.
 *
 * Tauri spells it two ways and BOTH have bitten us:
 *   - `tauri://localhost`      (Linux/macOS custom protocol) → hostname `localhost`
 *   - `http://tauri.localhost` (Android/Windows)             → hostname `tauri.localhost`
 *
 * The first one is the trap: its hostname is plain `localhost`, so every
 * "am I on a local origin?" check says yes and the resolvers hand back the
 * WebView's own origin. In a packaged build there is no Vite proxy behind that
 * origin, so every API call becomes `tauri://localhost/api/...` and the Rust
 * proxy rejects it with `builder error for url`. Measured on a real Linux
 * bundle on 2026-08-16 (listto-app): the app reported "API Server (Writes):
 * Service unreachable" and login was impossible.
 *
 * It lives HERE, next to the resolvers that need it, so the knowledge of what a
 * native WebView origin looks like exists in exactly one place: adding a third
 * spelling later must not require touching any call site.
 */
export function isNativeWebViewOrigin(location: { hostname: string; protocol: string }): boolean {
  return location.protocol === "tauri:" || location.hostname === "tauri.localhost";
}

/**
 * Detects if the current URL points to a local server.
 */
export function isLocalServerUrl(url: string): boolean {
  return (
    url.includes("localhost") ||
    url.includes("127.0.0.1") ||
    url.includes("192.168.") ||
    url.includes("10.") ||
    url.includes("172.")
  );
}

export interface ServerUrlConfig {
  isWeb: boolean;
  isDev: boolean;
  serverUrl: string;
  powerSyncUrl?: string;
  devMachineHost?: string | null;
  location?: {
    hostname: string;
    protocol: string;
    origin: string;
  };
}

/**
 * Extracts the port from a URL string.
 */
export function getPortFromUrl(urlStr: string | undefined, defaultPort = "3000"): string {
  if (!urlStr) {
    return defaultPort;
  }
  try {
    const parseUrl = urlStr.includes("://") ? urlStr : `http://${urlStr}`;
    const url = new URL(parseUrl);
    return url.port || defaultPort;
  } catch {
    return defaultPort;
  }
}

/**
 * Agnostic server URL resolution logic.
 */
export function resolveServerUrl(config: ServerUrlConfig): string {
  const { isWeb, isDev, serverUrl, location, devMachineHost } = config;

  // Electron proxy check (if on web and specific port)
  if (isWeb && location && !isNativeWebViewOrigin(location)) {
    if (location.hostname === "127.0.0.1" && location.origin.includes(":40031")) {
      return location.origin;
    }

    if (isLocalIP(location.hostname)) {
      // In web development, use the same origin to leverage Vite's API proxy.
      // This ensures cookies work (same-origin) without CORS issues.
      return location.origin;
    }

    return serverUrl;
  }

  // Native
  if (isDev && isLocalServerUrl(serverUrl) && devMachineHost) {
    const port = getPortFromUrl(serverUrl, "3000");
    return `http://${devMachineHost}:${port}`;
  }

  return serverUrl;
}

/**
 * Resolves a stored attachment/storage URL into an absolute URL the current
 * platform can actually open, given the backend origin (`baseUrl`).
 *
 * Shared by every platform (web browser, Tauri, native) so the resolution
 * rules stay in one place. Handles every shape we may encounter:
 *   - absolute `http(s)://…`     → used as-is (Rust backend returns these)
 *   - `blob:` / `data:`          → in-session refs, used as-is
 *   - `/api/storage/<key>`       → prefixed with `baseUrl` (Go backend shape)
 *   - bare `<key>`               → expanded to `<baseUrl>/api/storage/<key>`
 *
 * The origin prefix is what makes downloads work where the WebView/app origin
 * differs from the backend (Tauri at `tauri.localhost`, cross-origin web,
 * native devices).
 */
export function buildStorageUrl(baseUrl: string, url: string | null | undefined): string {
  if (!url) {
    return "";
  }
  if (/^(?:https?:|blob:|data:)/iu.test(url)) {
    return url;
  }
  const base = baseUrl.replace(/\/$/u, "");
  const path = url.startsWith("/") ? url : `/api/storage/${url}`;
  return `${base}${path}`;
}

/**
 * Agnostic PowerSync URL resolution logic.
 */
export function resolvePowerSyncUrl(config: ServerUrlConfig): string {
  const { isWeb, isDev, powerSyncUrl, location, devMachineHost } = config;
  const baseUrl = powerSyncUrl ?? "http://localhost:8085";

  if (isWeb && location && !isNativeWebViewOrigin(location) && isLocalIP(location.hostname)) {
    const port = getPortFromUrl(baseUrl, "8085");
    return `${location.protocol}//${location.hostname}:${port}`;
  }

  if (isDev && !isWeb) {
    const isLocal = !powerSyncUrl || isLocalServerUrl(powerSyncUrl);
    if (isLocal && devMachineHost) {
      const port = getPortFromUrl(baseUrl, "8085");
      return `http://${devMachineHost}:${port}`;
    }
  }

  return baseUrl;
}
